import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, ArrowRight, CheckCircle } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Wait for Google SDK to be ready
  useEffect(() => {
    const checkGoogleReady = () => {
      if (window.google?.accounts?.id) {
        setGoogleReady(true);
      }
    };
    
    checkGoogleReady();
    const interval = setInterval(checkGoogleReady, 100);
    
    setTimeout(() => {
      clearInterval(interval);
      setGoogleReady(true);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed. Please check your credentials.';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch {
          if (response.status === 401) {
            errorMessage = 'Incorrect email or password. Please try again.';
          } else if (response.status === 400) {
            errorMessage = 'Invalid login request. Please check your input.';
          } else if (response.status === 403) {
            errorMessage = 'Your account has been deactivated. Please contact your administrator.';
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Login successful!');
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user info from Google');
        }
        
        const userInfo = await userInfoResponse.json();
        
        const response = await fetch(`${BACKEND_URL}/api/auth/google/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            credential: tokenResponse.access_token,
            user_info: userInfo
          }),
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Google sign-in failed');
        }

        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          toast.success('Signed in with Google successfully!');
          navigate('/');
        }
      } catch (error) {
        console.error('Google login error:', error);
        toast.error(error.message || 'Google sign-in failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Login Error:', error);
      setGoogleLoading(false);
      
      if (error?.type === 'popup_closed') {
        toast.info('Sign-in cancelled');
        return;
      }
      
      toast.error('Google sign-in failed. Please try again or use email.');
    },
    flow: 'implicit',
  });

  const handleGoogleClick = () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    
    setTimeout(() => {
      googleLogin();
    }, 100);
  };

  const features = [
    'Create & Broadcast RFQs in minutes',
    'Compare quotes with L1/L2/L3 ranking',
    'Track supplier performance',
    'Generate Purchase Orders instantly'
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Feature Showcase */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)`
          }}></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/images/cloudrfq-logo.png" 
              alt="CloudRFQ" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-2xl font-bold">CloudRFQ</span>
          </div>
          
          <h1 className="text-4xl font-bold leading-tight mb-6">
            Streamline Your<br />
            <span className="text-blue-400">Procurement Process</span>
          </h1>
          
          <p className="text-lg text-slate-300 mb-10 max-w-md">
            Request, collect, and compare quotations from suppliers. Make data-driven purchasing decisions with ease.
          </p>
          
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <CheckCircle size={14} className="text-blue-400" />
                </div>
                <span className="text-slate-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom Stats */}
        <div className="relative z-10 grid grid-cols-3 gap-6 pt-8 border-t border-slate-700/50">
          <div>
            <div className="text-3xl font-bold text-blue-400">500+</div>
            <div className="text-sm text-slate-400">Active Users</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-400">10K+</div>
            <div className="text-sm text-slate-400">RFQs Processed</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-400">25%</div>
            <div className="text-sm text-slate-400">Time Saved</div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img 
              src="/images/cloudrfq-logo.png" 
              alt="CloudRFQ" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-bold text-slate-900">CloudRFQ</span>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="login-title">
                Welcome Back
              </h2>
              <p className="text-slate-500">Sign in to continue to your dashboard</p>
            </div>

            {/* Google Sign In */}
            <div className="mb-6" data-testid="google-signin-btn">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 text-base font-medium border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                onClick={handleGoogleClick}
                disabled={googleLoading || !googleReady}
              >
                {googleLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                    Signing in...
                  </div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-slate-400 font-medium">Or sign in with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                <div className="relative mt-1.5">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="you@company.com"
                    className="pl-10 h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <div className="relative mt-1.5">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="Enter your password"
                    className="pl-10 h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium transition-all" 
                disabled={loading} 
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Sign In
                    <ArrowRight size={18} />
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Don't have an account?{' '}
                <Link 
                  to="/signup" 
                  className="font-semibold text-blue-600 hover:text-blue-700 transition-colors" 
                  data-testid="signup-link"
                >
                  Create account
                </Link>
              </p>
            </div>
          </div>
          
          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
